import { atom } from "jotai";
import {
    incomeStatementMetricsAtom,
    incomeStatementResolutionAtom,
} from "../../../metrics.state";
import { FinanceLabField, formatFinanceLabValue } from "@/entities/FinanceLab";
import { getMetric } from "../../../utils/getMetric";
import { getPeriodColumns, getUniquePeriods } from "../../../utils/periods";
import { filterPeriodsByWindow } from "../../../utils/filterPeriodsByWindow";
import { HeaderWithTooltip } from "../../../components/HeaderWithTooltip";
import {
    calculateDelta,
    getDeltaLabel,
    getDeltaPeriodCount,
    getPeriodSlices,
} from "../../../utils/deltas";
import { ColDef } from "ag-grid-enterprise";
import { DeltaCellRenderer } from "../DeltaCellRenderer";
import { RowConfigWithMetric, SummaryRow } from "../SummaryFinancials.types";
import { rowConfig } from "./rowConfig";

export const ISSummaryMetricsAtom = atom<RowConfigWithMetric[]>((get) => {
    const incomeStatementMetrics = get(incomeStatementMetricsAtom);

    return rowConfig.map((row) => {
        return {
            metric: getMetric(
                incomeStatementMetrics,
                row.codes[0] as string,
                row.codes[1] as FinanceLabField,
            ),
            ...row,
        };
    });
});

export const ISSummaryPeriodsAtom = atom<string[]>((get) => {
    const metrics = get(ISSummaryMetricsAtom);
    const metricResolution = get(incomeStatementResolutionAtom);
    const periods = filterPeriodsByWindow(
        getUniquePeriods(
            metrics.map((m) => m.metric).filter((m) => m !== undefined),
        ),
        metricResolution.window,
    );
    return periods;
});

export interface PeriodColumn {
    field: string;
    headerName: string;
    headerComponent?: any;
    headerComponentParams?: any;
}

// Column definitions for the income statement summary table
export const ISSummaryColumnAtom = atom<ColDef[]>((get) => {
    const periods = get(ISSummaryPeriodsAtom);
    const periodColumns = getPeriodColumns(periods);
    const metricResolution = get(incomeStatementResolutionAtom);

    return [
        {
            field: "label",
            headerName: "",
            flex: 1.5,
            pinned: "left",
            cellClass: "heading-s",
        },
        ...periodColumns,
        {
            field: "popChange",
            pinned: "right",
            maxWidth: 150,
            headerName: getDeltaLabel(metricResolution.window || ""),
            headerComponent: HeaderWithTooltip,
            cellRenderer: DeltaCellRenderer,
            headerComponentParams: {
                tooltipText:
                    "Shows the % difference between the latest two available periods. Eg. if the last closed period is Q1-25, the delta shows the difference between Q1-25 and Q4-24.",
            },
        },
    ];
});

export const ISSummaryDataAtom = atom<SummaryRow[]>((get) => {
    const rows = get(ISSummaryMetricsAtom);
    const periods = get(ISSummaryPeriodsAtom);
    const metricResolution = get(incomeStatementResolutionAtom);
    const deltaPeriodCount = getDeltaPeriodCount(metricResolution.window || "");
    return rows.map((r) => {
        const row: SummaryRow = { ...r, previousSlice: [], currentSlice: [] };
        const returnBlankRow = () => {
            periods.forEach((period) => {
                row[period] = "-";
            });
            row.popChange = null;
            return row;
        };

        if (!r.metric) return returnBlankRow();

        const valuesByPeriod = new Map(
            r.metric.values.map((v) => [v.period, v.value]),
        );
        const slices = getPeriodSlices(
            deltaPeriodCount || 1,
            Array.from(valuesByPeriod.entries()),
        );
        const delta = calculateDelta(
            slices?.current || [],
            slices?.previous || [],
        );

        if (slices && delta !== null) {
            row.popChange = delta;
            row.previousSlice = slices.previous;
            row.currentSlice = slices.current;
        }

        valuesByPeriod.forEach((value, period) => {
            row[period] =
                value !== undefined
                    ? formatFinanceLabValue(value, row.fieldType)
                    : "-";
        });

        return row;
    });
});
